import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { closeModal, openModal, setShouldRefresh } from 'features/utilsSlice';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  Grid,
  IconButton,
  InputLabel,
  TextField,
  InputAdornment,
  TextareaAutosize,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { emailRegex } from 'utils/helpers';
import { postRequest, putRequest } from 'utils/api';
import useModalOpen from 'hooks/useModalOpen';
import useModalData from 'hooks/useModalData';
import CloseIcon from 'public/icons/close-main.svg';
import Password from 'public/icons/form/password.svg';
import PasswordEyeIcon from 'public/icons/eye-purple.svg';
import QuestionIcon from 'public/icons/question-circle-dark.svg';
import SlashEyeIcon from 'public/icons/eye-hide-purple.svg';
import TransitionDialog from 'components/TransitionDialog';
import SimpleDialog from 'components/SimpleDialog';
import BottomButtons from 'components/BottomButtons';
import IOSSwitch from 'components/IOSSwitch';
import ExternalLink from 'components/ExternalLink';
import ActionCable from 'components/ActionCable/Cable';
import toast from 'utils/toast';
import IconMainContainer from 'components/IconMainContainer';
import styles from 'styles/components/profileVerification/OnlyFansVerificationDialog.module.scss';

const mainColor = { fill: '#5b00db' };

const useStyles = makeStyles(theme => ({
  paper: {
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },

    [theme.breakpoints.up('lg')]: {
      width: '100%',
    },
  },

  paperSideWidth: {
    [theme.breakpoints.up('md')]: {
      width: '50%',
      marginLeft: 'auto',
    },

    [theme.breakpoints.up('lg')]: {
      width: '35%',
    },
  },

  paperFullScreenDialog: {
    width: '100%',
  },

  root: {
    width: '100%',
  },
}));

const OnlyFansVerificationDialog = ({
  title,
  subtitle,
  sideWidth,
  description,
  handleSuccess,
  leftBtnText,
}) => {
  const [haveTwoFactorAuth, setHaveTwoFactorAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    email: '',
    backupCodes: '',
    termsOfUse: false,
  });

  const dispatch = useDispatch();
  const open = useModalOpen('OnlyFans Verification');
  const { connectionId, influencerNetworkId } = useModalData('OnlyFans Verification');
  const router = useRouter();

  const classes = useStyles();

  const hasError = formData.email && !formData.email.match(emailRegex) ? true : false;

  const explanation = (
    <div style={{ textAlign: 'left' }}>
      <p style={{ marginBottom: 10 }}>
        To find your backup codes, go to your OnlyFans profile and click on the following:
      </p>

      <div className="color-main">&gt; Edit profile button</div>
      <div className="color-main">&gt; Account tab</div>
      <div className="color-main">&gt; Two Step Authentication tab</div>
      <div className="color-main">&gt; Show codes button</div>
    </div>
  );
  const handleClose = () => dispatch(closeModal('OnlyFans Verification'));

  const handleDialogClose = () => dispatch(closeModal('Backup Codes'));

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleSubmit = () => {
    setIsLoading(true);

    let endpoint = 'only_fans_connections';

    if (connectionId) {
      endpoint = `only_fans_connections/${connectionId}`;
    } else if (influencerNetworkId) {
      endpoint = `influencer_networks/${influencerNetworkId}/verify_only_fans`;
    }

    const request = connectionId ? putRequest : postRequest;

    request({
      endpoint,
      data: {
        email: formData.email,
        password: formData.password,
        backup_codes: formData.backupCodes.split('\n'),
        terms: formData.termsOfUse,
        two_factor: haveTwoFactorAuth,
      },
    })
      .then(() => {})
      .catch(() => {
        setIsLoading(false);
      });
  };

  const isEmpty = !formData.email || !formData.password || !formData.termsOfUse;

  const disabled = isEmpty || hasError || (haveTwoFactorAuth && !formData.backupCodes);

  useEffect(() => {
    if (!haveTwoFactorAuth) setFormData(prevState => ({ ...prevState, backupCodes: '' }));

    if (!open) {
      setHaveTwoFactorAuth(false);
      setFormData({ password: '', email: '', backupCodes: '' });
    }
  }, [haveTwoFactorAuth, open]);

  return (
    <>
      <TransitionDialog
        open={open}
        handleClose={handleClose}
        paperProps={{ className: sideWidth ? classes.paperSideWidth : classes.paper }}
        dialogStyles={classes.paperFullScreenDialog}
        styles={styles}
        noCloseIcon
      >
        <Grid
          container
          className={clsx(styles.profileVerification, { [styles.sideWidth]: sideWidth })}
          justifyContent="center"
        >
          <Grid item xs={11} className={styles.content}>
            <IconButton onClick={handleClose} className={styles.closeIcon}>
              <CloseIcon />
            </IconButton>

            <h3>{title}</h3>

            <h2>{subtitle}</h2>

            <div style={{ marginBottom: 15, padding: '0 5px' }}>{description}</div>

            <form style={{ textAlign: 'left', marginTop: 30 }}>
              <InputLabel>E-mail</InputLabel>

              <TextField
                type="email"
                name="email"
                value={formData.email}
                placeholder="Email Address"
                onChange={e => setFormData(prevState => ({ ...prevState, email: e.target.value }))}
                variant="outlined"
                error={hasError}
                helperText={hasError ? 'is not valid.' : ''}
                autoComplete="username"
              />

              <InputLabel>Password</InputLabel>

              <TextField
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={e =>
                  setFormData(prevState => ({ ...prevState, password: e.target.value }))
                }
                placeholder="Password"
                autoComplete="current-password"
                variant="outlined"
                color="primary"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Password width={14} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <div
                        onClick={handleClickShowPassword}
                        style={{ height: 20, cursor: 'pointer' }}
                      >
                        {showPassword ? (
                          <PasswordEyeIcon width={20} height={20} />
                        ) : (
                          <SlashEyeIcon style={mainColor} width={20} height={20} />
                        )}
                      </div>
                    </InputAdornment>
                  ),
                }}
              />

              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <InputLabel style={{ margin: 0 }}>Do you use two-factor authentication?</InputLabel>

                <div style={{ marginRight: -8 }}>
                  <IOSSwitch
                    checked={haveTwoFactorAuth}
                    onChange={e => setHaveTwoFactorAuth(e.target.checked)}
                    name="twoFactorAuth"
                  />
                </div>
              </div>
            </form>

            {haveTwoFactorAuth && (
              <div style={{ textAlign: 'left', marginTop: 20 }}>
                <div className={styles.label}>
                  <InputLabel>Enter/paste your backup codes</InputLabel>

                  <IconButton
                    onClick={() => dispatch(openModal({ name: 'Backup Codes' }))}
                    className="hover-icon"
                  >
                    <QuestionIcon />
                  </IconButton>
                </div>

                <TextareaAutosize
                  name="backupCodes"
                  value={formData.backupCodes}
                  onChange={e =>
                    setFormData(prevState => ({ ...prevState, backupCodes: e.target.value }))
                  }
                  minRows={5}
                  maxRows={5}
                  placeholder={`E.g. ${'\n'}12346 29313 ${'\n'}65421 34423 ${'\n'}62121 12036 ${'\n'}...`}
                />
              </div>
            )}

            <FormControlLabel
              control={<Checkbox color="primary" />}
              label={
                <>
                  I agree to the{' '}
                  <ExternalLink
                    preview="url"
                    mobileUrl="/terms"
                    desktopUrl="/terms"
                    target="_blank"
                    rel="noreferrer noopener"
                    page="Terms of Service"
                  >
                    terms of service
                  </ExternalLink>{' '}
                  and{' '}
                  <ExternalLink
                    preview="url"
                    mobileUrl="/privacy"
                    desktopUrl="/privacy"
                    target="_blank"
                    rel="noreferrer noopener"
                    page="Privacy Policy"
                  >
                    privacy policy
                  </ExternalLink>
                </>
              }
              onChange={e =>
                setFormData(prevState => ({ ...prevState, termsOfUse: e.target.checked }))
              }
              className={classes.root}
            />
          </Grid>

          <BottomButtons
            text={leftBtnText ? leftBtnText : 'Cancel'}
            textContained="Done"
            handleClose={handleClose}
            handleClick={handleSubmit}
            disabled={disabled}
            isLoading={isLoading}
            textForLoading="Estimated wait time: 2 min.."
            newStyles={!sideWidth ? styles : {}}
            fixed
          />
        </Grid>

        <SimpleDialog
          modalName="Backup Codes"
          description={explanation}
          onClick={handleDialogClose}
          buttonText="Got it"
        />

        <ActionCable
          channel="VerifyOnlyFansChannel"
          onReceived={({ type, payload, processing_type }) => {
            if (type === 'success') {
              setIsLoading(false);

              if (processing_type === 'sync') {
                handleSuccess(payload);
              } else {
                toast(
                  'success',
                  'Your connection is processing in the background. Please visit the of-connections page later to check the result.',
                  {
                    icon: (
                      <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />
                    ),
                  }
                );

                if (router.pathname === '/of-connections') {
                  // To trigger a refresh on the of-connections page
                  dispatch(setShouldRefresh(new Date().toString()));
                }
              }

              handleClose();
            } else {
              setIsLoading(false);
              toast(
                'error',
                payload?.errors?.[0] ||
                  'There was an error verifying your OnlyFans account. Please try again or contact support.',
                {
                  icon: (
                    <IconMainContainer
                      src="/icons/notifications/times.svg"
                      alt="Times"
                      errorStyle
                    />
                  ),
                }
              );
            }
          }}
        />
      </TransitionDialog>
    </>
  );
};

OnlyFansVerificationDialog.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  handleSuccess: PropTypes.func,
  sideWidth: PropTypes.bool,
  leftBtnText: PropTypes.string,
};

OnlyFansVerificationDialog.defaultProps = {
  description: '',
  sideWidth: false,
  handleSuccess: () => {},
  leftBtnText: '',
};

export default OnlyFansVerificationDialog;
